import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download, File } from "lucide-react";

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data.items);
    } catch {
      toast.error("No se pudieron cargar los documentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo supera el límite de 10MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${file.name}" subido correctamente`);
      if (data.knowledge_status !== "ready") toast.warning("Archivo guardado sin texto para Sofi. Usa PDF con texto, DOCX o TXT.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDownload = async (doc) => {
    let url;
    try {
      const { data } = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      url = URL.createObjectURL(data);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = doc.name;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
    } catch {
      toast.error("No se pudo descargar el documento");
    } finally {
      if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.name}"?`)) return;
    setDeleting(doc.id);
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Documento eliminado");
      setDocs(d => d.filter(x => x.id !== doc.id));
    } catch {
      toast.error("No se pudo eliminar");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-4xl mx-auto w-full">
      <PageHeader
        title="Documentos"
        subtitle="PDF con texto, DOCX y TXT aportan conocimiento a Sofi. No subas expedientes ni datos de pacientes."
        actions={
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".txt,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              className="hidden"
              onChange={handleUpload}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 bg-brand-electric text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {uploading ? "Subiendo…" : "Subir documento"}
            </button>
          </>
        }
      />

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400 text-sm animate-pulse">
          Cargando documentos…
        </div>
      ) : docs.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="font-medium text-slate-500">Sin documentos</div>
          <p className="text-sm text-slate-400 mt-1">Sube contratos, protocolos o cualquier archivo de tu clínica.</p>
          <button
            onClick={() => fileRef.current?.click()}
            className="mt-4 text-sm text-brand-electric hover:underline"
          >
            Subir primer documento
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Nombre</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">Tamaño</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">Subido</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">Por</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {docs.map(doc => (
                <tr key={doc.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <File className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-800 truncate max-w-[220px]">{doc.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-500">{fmtSize(doc.size)}</td>
                  <td className="px-4 py-3 text-center text-slate-500">{fmtDate(doc.uploaded_at)}</td>
                  <td className="px-4 py-3 text-center text-xs text-slate-400 truncate max-w-[140px]">{doc.uploaded_by || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Descargar"
                      >
                        <Download size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        disabled={deleting === doc.id}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-40"
                        title="Eliminar"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
